import {ConsentLevel} from '../app/context/CookieConsentContext'

const COOKIE_DEFAULTS = {
  path: '/',
  sameSite: 'Lax' as const,
  secure: window.location.protocol === 'https:',
}

const EXPIRY_DAYS = 30

function setCookie(name: string, value: string, days: number = EXPIRY_DAYS) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  const parts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    `expires=${expires}`,
    `path=${COOKIE_DEFAULTS.path}`,
    `SameSite=${COOKIE_DEFAULTS.sameSite}`,
  ]
  if (COOKIE_DEFAULTS.secure) parts.push('Secure')
  document.cookie = parts.join('; ')
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
  )
  return match ? decodeURIComponent(match[1]) : null
}

function removeCookie(name: string) {
  document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}

// ── Cookie keys ──

const KEYS = {
  USER_TYPE: 'aac_user_type',
  USER_EMAIL: 'aac_user_email',
  USER_NAME: 'aac_user_name',
  USER_ID: 'aac_user_id',
  AUTH_TOKEN: 'aac_auth_token',
  AWS_USER_ID: 'aac_aws_user_id',
  AWS_CONNECT_TOKEN: 'aac_aws_connect_token',
  AWS_CONNECT_URL: 'aac_aws_connect_url',
  CAMERA_PERMISSION: 'aac_perm_camera',
  MICROPHONE_PERMISSION: 'aac_perm_microphone',
  LOCATION_PERMISSION: 'aac_perm_location',
  NOTIFICATION_PERMISSION: 'aac_perm_notification',
  TIMEZONE: 'aac_timezone',
  THEME: 'aac_theme',
  LOCALE: 'aac_locale',
} as const

// ── Public API ──

export interface UserCookieData {
  userType?: string
  email?: string
  firstName?: string
  lastName?: string
  userId?: string
  authToken?: string
  awsUserId?: string
  awsConnectToken?: string
  timezone?: string
}

export function saveEssentialCookies(data: UserCookieData) {
  if (data.userType) setCookie(KEYS.USER_TYPE, data.userType)
  if (data.email) setCookie(KEYS.USER_EMAIL, data.email)
  if (data.firstName || data.lastName) {
    setCookie(KEYS.USER_NAME, `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim())
  }
  if (data.userId) setCookie(KEYS.USER_ID, data.userId)
  if (data.authToken) setCookie(KEYS.AUTH_TOKEN, data.authToken, 1)
  if (data.awsUserId) setCookie(KEYS.AWS_USER_ID, data.awsUserId)
  if (data.awsConnectToken) setCookie(KEYS.AWS_CONNECT_TOKEN, data.awsConnectToken, 1)
  if (data.timezone) setCookie(KEYS.TIMEZONE, data.timezone)

  const awsUrl = process.env.REACT_APP_AWS_CONNECT_URL
  if (awsUrl) setCookie(KEYS.AWS_CONNECT_URL, awsUrl)
}

export async function savePermissionCookies() {
  try {
    if (navigator.permissions) {
      const mic = await navigator.permissions.query({name: 'microphone' as PermissionName})
      setCookie(KEYS.MICROPHONE_PERMISSION, mic.state)
    }
  } catch {
    setCookie(KEYS.MICROPHONE_PERMISSION, 'unknown')
  }

  try {
    if (navigator.permissions) {
      const cam = await navigator.permissions.query({name: 'camera' as PermissionName})
      setCookie(KEYS.CAMERA_PERMISSION, cam.state)
    }
  } catch {
    setCookie(KEYS.CAMERA_PERMISSION, 'unknown')
  }

  try {
    if (typeof window !== 'undefined' && typeof window.Notification !== 'undefined') {
      setCookie(KEYS.NOTIFICATION_PERMISSION, window.Notification.permission)
    }
  } catch {
    setCookie(KEYS.NOTIFICATION_PERMISSION, 'unknown')
  }

  try {
    if (navigator.geolocation) {
      const geo = await navigator.permissions.query({name: 'geolocation'})
      setCookie(KEYS.LOCATION_PERMISSION, geo.state)
    }
  } catch {
    setCookie(KEYS.LOCATION_PERMISSION, 'unknown')
  }
}

export function saveOptionalCookies() {
  setCookie(KEYS.THEME, document.documentElement.getAttribute('data-bs-theme') || 'light')
  setCookie(KEYS.LOCALE, navigator.language || 'en')
}

export function clearAllAppCookies() {
  Object.values(KEYS).forEach((key) => removeCookie(key))
}

export function clearOptionalCookies() {
  removeCookie(KEYS.THEME)
  removeCookie(KEYS.LOCALE)
}

const TEST_COOKIE_NAME = 'aac_cookie_test'

/**
 * Verifies that the browser allows setting cookies for this site.
 * Call after consent so we know if essential cookies can be stored.
 */
export function ensureCookiesWritable(): boolean {
  try {
    setCookie(TEST_COOKIE_NAME, '1', 0)
    const read = getCookie(TEST_COOKIE_NAME)
    removeCookie(TEST_COOKIE_NAME)
    return read === '1'
  } catch {
    return false
  }
}

/**
 * Requests browser notification permission so users can receive AWS Connect
 * call popups. Required for incoming call alerts; ensure system Do Not Disturb
 * is off for notifications to appear. Updates the notification permission cookie.
 */
export async function requestNotificationPermissionForConnect(): Promise<NotificationPermission> {
  try {
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
      setCookie(KEYS.NOTIFICATION_PERMISSION, 'unsupported')
      return 'denied'
    }
    const current = window.Notification.permission
    if (current === 'granted') {
      setCookie(KEYS.NOTIFICATION_PERMISSION, 'granted')
      return 'granted'
    }
    if (current === 'denied') {
      setCookie(KEYS.NOTIFICATION_PERMISSION, 'denied')
      return 'denied'
    }
    const permission = await window.Notification.requestPermission()
    setCookie(KEYS.NOTIFICATION_PERMISSION, permission)
    return permission
  } catch {
    setCookie(KEYS.NOTIFICATION_PERMISSION, 'unknown')
    return 'denied'
  }
}

export function applyCookieConsent(level: ConsentLevel, userData?: UserCookieData) {
  if (!level) return

  if (userData) {
    saveEssentialCookies(userData)
  }
  savePermissionCookies()

  if (level === 'all') {
    saveOptionalCookies()
  } else {
    clearOptionalCookies()
  }
}

export function getEssentialCookies() {
  return {
    userType: getCookie(KEYS.USER_TYPE),
    email: getCookie(KEYS.USER_EMAIL),
    userName: getCookie(KEYS.USER_NAME),
    userId: getCookie(KEYS.USER_ID),
    awsUserId: getCookie(KEYS.AWS_USER_ID),
    awsConnectToken: getCookie(KEYS.AWS_CONNECT_TOKEN),
    awsConnectUrl: getCookie(KEYS.AWS_CONNECT_URL),
    cameraPermission: getCookie(KEYS.CAMERA_PERMISSION),
    microphonePermission: getCookie(KEYS.MICROPHONE_PERMISSION),
    locationPermission: getCookie(KEYS.LOCATION_PERMISSION),
    notificationPermission: getCookie(KEYS.NOTIFICATION_PERMISSION),
    timezone: getCookie(KEYS.TIMEZONE),
  }
}

export {setCookie, getCookie, removeCookie, KEYS}
